/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect, useState} from 'react'
import moment from 'moment'
import Swal from 'sweetalert2'
import {PageTitle} from '../../../../_metronic/layout/core'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'
import {Button} from '../../../components/Button/Button'
import {Datatable} from '../../../components/Datatable/Datatable'
import {Input} from '../../../components/Input/Input'
import {Loading} from '../../../components/Loading/Loading'
import {ModalWrapper} from '../../../components/Modals/General/ModalWrapper/ModalWrapper'
import useAuth from '../../../hooks/useAuth'
import useModals from '../../../hooks/useModals'
import {POST} from '../../../services/api'
import {formatHotelPointsSource} from './hotelPointsLabels'

type HotelPointsTransactionType = 'earn' | 'redeem' | 'reversal' | 'expire'
type ModalMode = 'detail' | 'reversal' | 'create'

type HotelPointsTransactionRow = {
  id: number
  customerId: number
  type: string
  typeRaw: HotelPointsTransactionType
  amount: string
  amountRaw: number
  source: string
  sourceType: string | null
  sourceId: number | null
  reservationId: number | null
  reservationLabel: string
  hasReservation: boolean
  description: string
  customerName: string
  created: string
  canReverse: boolean
}

type FilterState = {
  type: string
  amount: string
  createdFrom: string
  createdTo: string
}

const TYPE_OPTIONS = [
  {value: '', select: 'Todos'},
  {value: 'earn', select: 'Acúmulo'},
  {value: 'redeem', select: 'Resgate'},
  {value: 'reversal', select: 'Estorno'},
  {value: 'expire', select: 'Expiração'},
]

const TYPE_LABELS: Record<HotelPointsTransactionType, string> = {
  earn: 'Acúmulo',
  redeem: 'Resgate',
  reversal: 'Estorno',
  expire: 'Expiração',
}

const CREATE_PROFILES = ['admin', 'owner', 'manager']

const INITIAL_FILTERS: FilterState = {
  type: '',
  amount: '',
  createdFrom: '',
  createdTo: '',
}

const extractList = (response: any): any[] => {
  if (Array.isArray(response?.data)) return response.data
  if (Array.isArray(response?.dados)) return response.dados
  if (Array.isArray(response)) return response
  return []
}

const translateType = (type: string) => {
  return TYPE_LABELS[type as HotelPointsTransactionType] || type || '-'
}

const formatSource = (sourceType?: string | null, sourceId?: number | null) => {
  return formatHotelPointsSource(sourceType, sourceId)
}

const buildFilterPayload = (filters: FilterState) => {
  const payload: Record<string, string | number> = {}
  if (filters.type) payload.type = filters.type
  if (filters.amount) payload.amount = Number(filters.amount)
  if (filters.createdFrom) {
    payload.createdFrom = moment(filters.createdFrom).startOf('day').toISOString()
  }
  if (filters.createdTo) {
    payload.createdTo = moment(filters.createdTo).endOf('day').toISOString()
  }
  return payload
}

const mapTransactions = (data: any[]): HotelPointsTransactionRow[] => {
  return data.map((item) => {
    const sourceType = item?.sourceType || null
    const sourceId = item?.sourceId ?? null
    const typeRaw = (item?.type || 'earn') as HotelPointsTransactionType

    const reservationId = item?.reservationId ? Number(item.reservationId) : null

    return {
      id: Number(item?.id || 0),
      customerId: Number(
        item?.customerId ||
          item?.customerHotelPoints?.customerId ||
          item?.customerHotelPoints?.customer?.id ||
          0
      ),
      type: translateType(typeRaw),
      typeRaw,
      amount: String(item?.amount ?? 0),
      amountRaw: Number(item?.amount || 0),
      source: formatSource(sourceType, sourceId),
      sourceType,
      sourceId,
      reservationId,
      reservationLabel: reservationId ? `#${reservationId}` : '-',
      hasReservation: !!reservationId,
      description: item?.description || '-',
      customerName: item?.customerHotelPoints?.customer?.name || '-',
      created: item?.created
        ? moment(item.created).utcOffset(-3).format('DD/MM/YYYY HH:mm:ss')
        : '-',
      canReverse: typeRaw === 'earn' && !!sourceType && !!sourceId,
    }
  })
}

const HotelPointsListContent: FC = () => {
  const {loginInfo} = useAuth()
  const {showWrapperModal, closeWrapperModal} = useModals()
  const canCreate = CREATE_PROFILES.includes(loginInfo?.profile || '')

  const [loading, setLoading] = useState(false)
  const [savingAction, setSavingAction] = useState(false)
  const [transactions, setTransactions] = useState<HotelPointsTransactionRow[]>([])
  const [filters, setFilters] = useState<FilterState>(INITIAL_FILTERS)
  const [modalMode, setModalMode] = useState<ModalMode | null>(null)
  const [selectedTransaction, setSelectedTransaction] = useState<HotelPointsTransactionRow | null>(
    null
  )
  const [reversalDescription, setReversalDescription] = useState('')
  const [descriptionError, setDescriptionError] = useState('')
  const [customerId, setCustomerId] = useState('')
  const [amount, setAmount] = useState('')
  const [createDescription, setCreateDescription] = useState('')
  const [createErrors, setCreateErrors] = useState<Record<string, string>>({})

  const setFilterField = <K extends keyof FilterState>(field: K, value: FilterState[K]) => {
    setFilters((prev) => ({...prev, [field]: value}))
  }

  const loadTransactions = async (currentFilters: FilterState = filters) => {
    setLoading(true)
    try {
      const response = await POST(
        '/hotel-points/transaction/filter',
        buildFilterPayload(currentFilters)
      )
      setTransactions(mapTransactions(extractList(response)))
    } catch (error) {
      console.error(error)
      setTransactions([])
      Swal.fire({
        icon: 'warning',
        title: 'Oops...',
        text: 'Erro ao carregar as movimentações de Pontos Hotel.',
      })
    } finally {
      setLoading(false)
    }
  }

  const clearFilters = () => {
    setFilters(INITIAL_FILTERS)
    loadTransactions(INITIAL_FILTERS)
  }

  const resetModalState = () => {
    setModalMode(null)
    setSelectedTransaction(null)
    setReversalDescription('')
    setDescriptionError('')
    setCustomerId('')
    setAmount('')
    setCreateDescription('')
    setCreateErrors({})
  }

  const openDetailModal = (item: HotelPointsTransactionRow) => {
    setSelectedTransaction(item)
    setModalMode('detail')
    showWrapperModal('Detalhes da movimentação')
  }

  const openReversalModal = (item: HotelPointsTransactionRow) => {
    if (!item.sourceType || !item.sourceId) {
      Swal.fire({
        icon: 'warning',
        title: 'Oops...',
        text: 'Esta movimentação não possui origem vinculada para estorno.',
      })
      return
    }

    setSelectedTransaction(item)
    setModalMode('reversal')
    setReversalDescription('')
    setDescriptionError('')
    showWrapperModal('Estornar Pontos Hotel')
  }

  const openCreateModal = () => {
    setSelectedTransaction(null)
    setCustomerId('')
    setAmount('')
    setCreateDescription('')
    setCreateErrors({})
    setModalMode('create')
    showWrapperModal('Novo crédito')
  }

  const closeModal = () => {
    closeWrapperModal()
    resetModalState()
  }

  const confirmReversal = async () => {
    if (!selectedTransaction?.sourceType || !selectedTransaction?.sourceId) {
      closeModal()
      Swal.fire({
        icon: 'warning',
        title: 'Oops...',
        text: 'Origem inválida para estorno.',
      })
      return
    }

    const description = reversalDescription.trim()
    if (!description || description.length < 10) {
      setDescriptionError('Informe o motivo do estorno (mínimo 10 caracteres).')
      return
    }

    try {
      setSavingAction(true)
      const response = await POST('/hotel-points/reversal', {
        sourceType: selectedTransaction.sourceType,
        sourceId: selectedTransaction.sourceId,
        description,
      })

      closeModal()
      await loadTransactions()

      if (response?.success === false) {
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: response?.message || 'Não foi possível estornar os pontos.',
        })
        return
      }

      Swal.fire({
        icon: 'success',
        title: 'Estorno realizado',
        text: response?.message || 'Os pontos foram estornados com sucesso.',
      })
    } catch (error) {
      console.error(error)
      closeModal()
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: String(error) ? String(error) : 'Não foi possível estornar os pontos.',
      })
    } finally {
      setSavingAction(false)
    }
  }

  const confirmCreate = async () => {
    const nextErrors: Record<string, string> = {}
    const parsedCustomerId = Number(customerId)
    const parsedAmount = Number(amount)

    if (!parsedCustomerId || parsedCustomerId <= 0) {
      nextErrors.customerId = 'Informe o ID do cliente.'
    }
    if (!Number.isInteger(parsedAmount) || parsedAmount <= 0) {
      nextErrors.amount = 'Informe uma quantidade inteira maior que zero.'
    }

    setCreateErrors(nextErrors)
    if (Object.keys(nextErrors).length > 0) return

    try {
      setSavingAction(true)
      const payload: Record<string, string | number> = {
        customerId: parsedCustomerId,
        type: 'earn',
        amount: parsedAmount,
        description: createDescription.trim() || 'Crédito manual de Pontos Hotel',
        sourceType: 'manual',
      }
      if (loginInfo?.pluginspaceId) {
        payload.pluginspaceId = loginInfo.pluginspaceId
      }

      const response = await POST('/hotel-points/transaction', payload)

      closeModal()
      await loadTransactions()

      if (response?.success === false) {
        Swal.fire({
          icon: 'error',
          title: 'Erro',
          text: response?.message || 'Não foi possível registrar o crédito.',
        })
        return
      }

      Swal.fire({
        icon: 'success',
        title: 'Crédito registrado',
        text: response?.message || 'A movimentação foi registrada com sucesso.',
      })
    } catch (error) {
      console.error(error)
      closeModal()
      Swal.fire({
        icon: 'error',
        title: 'Erro',
        text: String(error) ? String(error) : 'Não foi possível registrar o crédito.',
      })
    } finally {
      setSavingAction(false)
    }
  }

  useEffect(() => {
    loadTransactions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const modalConfirmAction = () => {
    if (modalMode === 'detail') {
      closeModal()
      return
    }
    if (modalMode === 'create') {
      void confirmCreate()
      return
    }
    void confirmReversal()
  }

  const modalTextAction =
    modalMode === 'detail'
      ? 'Fechar'
      : savingAction
      ? modalMode === 'create'
        ? 'Salvando...'
        : 'Estornando...'
      : modalMode === 'create'
      ? 'Salvar'
      : 'Confirmar'

  return (
    <>
      {loading ? (
        <Loading />
      ) : (
        <div className='row g-5 gx-xxl-12'>
          <div className='card card-xxl-stretch mb-5 mb-xl-12'>
            <div className='card-header py-5'>
              <h3 className='card-title align-items-start flex-column'>
                <span className='card-label fw-bolder fs-3 mb-1'>Movimentações</span>
                <BreadCrumbs />
                <span className='text-muted mt-1 fw-bold fs-7'>
                  Consulte o histórico de acúmulo, resgate, estorno e expiração de Pontos Hotel.
                </span>
              </h3>
              {canCreate && (
                <div className='card-toolbar'>
                  <Button
                    text='Novo crédito'
                    color='primary'
                    size='small'
                    icon='fas fa-plus'
                    horizontalPadding={6}
                    btnAttr={{type: 'button'}}
                    click={openCreateModal}
                    btnClass='fw-bolder'
                  />
                </div>
              )}
            </div>
            <div className='card-body'>
              <div className='w-100 mb-10'>
                <div className='row align-items-end g-5'>
                  <div className='col-md-3'>
                    <Input
                      inputAttr={{type: 'select'}}
                      label='Tipo'
                      value={filters.type}
                      change={(value: string) => setFilterField('type', value)}
                      options={TYPE_OPTIONS}
                      marginTop='0'
                    />
                  </div>
                  <div className='col-md-2'>
                    <Input
                      inputAttr={{type: 'number'}}
                      label='Pontos'
                      value={filters.amount}
                      change={(value: string) => setFilterField('amount', value)}
                      placeholder='Pontos'
                      marginTop='0'
                      minNumber={0}
                      allowFloat={false}
                    />
                  </div>
                  <div className='col-md-3'>
                    <Input
                      inputAttr={{type: 'date'}}
                      label='Data inicial'
                      value={filters.createdFrom}
                      change={(value: string) => setFilterField('createdFrom', value)}
                      maxLength={filters.createdTo}
                      marginTop='0'
                    />
                  </div>
                  <div className='col-md-3'>
                    <Input
                      inputAttr={{type: 'date'}}
                      label='Data final'
                      value={filters.createdTo}
                      change={(value: string) => setFilterField('createdTo', value)}
                      minLength={filters.createdFrom}
                      marginTop='0'
                    />
                  </div>
                  <div className='col-md-12 d-flex justify-content-md-end gap-3'>
                    <Button
                      color='primary'
                      icon='fa fa-filter'
                      text='Filtrar'
                      size='small'
                      click={() => loadTransactions()}
                      btnClass='mb-0'
                    />
                    <Button
                      color='danger'
                      icon='fa fa-eraser'
                      size='small'
                      text='Limpar'
                      click={clearFilters}
                      btnClass='mb-0'
                    />
                  </div>
                </div>
              </div>

              <Datatable
                data={transactions}
                headers={[
                  {key: 'Cliente', value: 'customerName', type: 'default'},
                  {
                    key: 'Tipo',
                    value: 'type',
                    type: 'badge',
                    badge: {
                      acúmulo: 'success',
                      resgate: 'warning',
                      estorno: 'danger',
                      expiração: 'secondary',
                    },
                  },
                  {key: 'Pontos', value: 'amount', type: 'default'},
                  {key: 'Origem', value: 'source', type: 'default'},
                  {key: 'Reserva', value: 'reservationLabel', type: 'default'},
                  {key: 'Descrição', value: 'description', type: 'default'},
                  {key: 'Data', value: 'created', type: 'default'},
                  {key: 'Ações', value: 'acoes', type: ''},
                ]}
                options={[
                  {
                    icon: 'bi bi-eye',
                    action: (item) => openDetailModal(item),
                    title: 'Ver detalhes',
                  },
                  {
                    icon: 'bi bi-door-open',
                    action: (item) =>
                      window.open(
                        `/hospitality/hotel-reservation/editar/${item.reservationId}`,
                        '_blank'
                      ),
                    title: 'Abrir reserva',
                    verifyConditionItem: 'hasReservation',
                    verifyConditionValue: true,
                  },
                  {
                    icon: 'bi bi-arrow-counterclockwise',
                    action: (item) => openReversalModal(item),
                    title: 'Estornar pontos',
                    verifyConditionItem: 'canReverse',
                    verifyConditionValue: true,
                  },
                ]}
                color='primary'
              />
            </div>
          </div>
        </div>
      )}

      <ModalWrapper
        confirmAction={modalConfirmAction}
        shouldCloseModal={modalMode === 'detail'}
        disableMainButton={savingAction}
        onCloseAction={resetModalState}
        textAction={modalTextAction}
        confirmButtomColor='primary'
        noCloseButton={modalMode === 'detail'}
        width={60}
      >
        {selectedTransaction && modalMode === 'detail' && (
          <div className='row g-4'>
            <div className='col-12'>
              <h6 className='fw-bold text-gray-800 mb-3'>
                <i className='bi bi-eye text-primary me-2'></i>
                Detalhes da movimentação
              </h6>
            </div>
            <div className='col-md-6'>
              <div className='text-muted fs-7 mb-1'>Cliente</div>
              <div className='fw-bold text-gray-800'>{selectedTransaction.customerName}</div>
            </div>
            <div className='col-md-6'>
              <div className='text-muted fs-7 mb-1'>Tipo</div>
              <div className='fw-bold text-gray-800'>{selectedTransaction.type}</div>
            </div>
            <div className='col-md-6'>
              <div className='text-muted fs-7 mb-1'>Pontos</div>
              <div className='fw-bold text-gray-800'>{selectedTransaction.amount}</div>
            </div>
            <div className='col-md-6'>
              <div className='text-muted fs-7 mb-1'>Origem</div>
              <div className='fw-bold text-gray-800'>{selectedTransaction.source}</div>
            </div>
            <div className='col-md-6'>
              <div className='text-muted fs-7 mb-1'>Data</div>
              <div className='fw-bold text-gray-800'>{selectedTransaction.created}</div>
            </div>
            <div className='col-md-6'>
              <div className='text-muted fs-7 mb-1'>Reserva</div>
              {selectedTransaction.hasReservation ? (
                <button
                  type='button'
                  className='btn btn-sm btn-light-primary'
                  onClick={() =>
                    window.open(
                      `/hospitality/hotel-reservation/editar/${selectedTransaction.reservationId}`,
                      '_blank'
                    )
                  }
                >
                  #{selectedTransaction.reservationId}
                </button>
              ) : (
                <div className='fw-bold text-gray-800'>-</div>
              )}
            </div>
            <div className='col-12 mb-12'>
              <div className='text-muted fs-7 mb-1'>Descrição</div>
              <div className='fw-bold text-gray-800'>{selectedTransaction.description}</div>
            </div>
          </div>
        )}

        {selectedTransaction && modalMode === 'reversal' && (
          <div className='row g-4'>
            <div className='col-12'>
              <h6 className='fw-bold text-gray-800 mb-3'>
                <i className='bi bi-receipt text-primary me-2'></i>
                Dados da movimentação
              </h6>
            </div>
            <div className='col-md-6'>
              <div className='text-muted fs-7 mb-1'>Cliente</div>
              <div className='fw-bold text-gray-800'>{selectedTransaction.customerName}</div>
            </div>
            <div className='col-md-6'>
              <div className='text-muted fs-7 mb-1'>Pontos</div>
              <div className='fw-bold text-gray-800'>{selectedTransaction.amount}</div>
            </div>
            <div className='col-md-6'>
              <div className='text-muted fs-7 mb-1'>Origem</div>
              <div className='fw-bold text-gray-800'>{selectedTransaction.source}</div>
            </div>
            <div className='col-md-6'>
              <div className='text-muted fs-7 mb-1'>Data</div>
              <div className='fw-bold text-gray-800'>{selectedTransaction.created}</div>
            </div>
            <div className='col-12'>
              <div className='separator separator-dashed'></div>
            </div>
            <div className='col-12'>
              <h6 className='fw-bold text-gray-800 mb-3'>
                <i className='bi bi-chat-left-text text-primary me-2'></i>
                Motivo do estorno
              </h6>
              <Input
                inputAttr={{type: 'textarea'}}
                label='Observação'
                placeholder='Informe o motivo do estorno destes pontos'
                value={reversalDescription}
                change={(value: string) => {
                  setReversalDescription(value)
                  if (descriptionError) setDescriptionError('')
                }}
                rows={4}
                required
              />
              {descriptionError && <div className='text-danger small mt-1'>{descriptionError}</div>}
            </div>
            <div className='col-12'>
              <div className='alert alert-warning d-flex align-items-start mb-0'>
                <i className='bi bi-exclamation-triangle-fill fs-4 me-3 mt-1'></i>
                <div>
                  O estorno será aplicado sobre a origem{' '}
                  <strong>{selectedTransaction.source}</strong>. A observação é obrigatória.
                </div>
              </div>
            </div>
          </div>
        )}

        {modalMode === 'create' && (
          <div className='row g-4'>
            <div className='col-12'>
              <h6 className='fw-bold text-gray-800 mb-3'>
                <i className='bi bi-plus-circle text-primary me-2'></i>
                Novo crédito
              </h6>
            </div>
            <div className='col-md-6'>
              <Input
                inputAttr={{type: 'number'}}
                label='ID do cliente'
                value={customerId}
                change={(value: string) => {
                  setCustomerId(value)
                  if (createErrors.customerId) {
                    setCreateErrors((prev) => {
                      const next = {...prev}
                      delete next.customerId
                      return next
                    })
                  }
                }}
                minNumber={1}
                allowFloat={false}
                marginTop='0'
              />
              {createErrors.customerId && (
                <div className='text-danger small mt-1'>{createErrors.customerId}</div>
              )}
            </div>
            <div className='col-md-6'>
              <Input
                inputAttr={{type: 'number'}}
                label='Pontos'
                value={amount}
                change={(value: string) => {
                  setAmount(value)
                  if (createErrors.amount) {
                    setCreateErrors((prev) => {
                      const next = {...prev}
                      delete next.amount
                      return next
                    })
                  }
                }}
                minNumber={1}
                allowFloat={false}
                marginTop='0'
              />
              {createErrors.amount && (
                <div className='text-danger small mt-1'>{createErrors.amount}</div>
              )}
            </div>
            <div className='col-12'>
              <Input
                inputAttr={{type: 'textarea'}}
                label='Descrição (opcional)'
                placeholder='Informe uma descrição para este crédito'
                value={createDescription}
                change={setCreateDescription}
                rows={3}
                marginTop='0'
              />
            </div>
          </div>
        )}
      </ModalWrapper>
    </>
  )
}

const HotelPointsList: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'Dashboard', path: 'dashboard', isActive: false},
          {title: 'Pontos Hotel', path: 'hotel-points', isActive: false},
          {title: 'Movimentações', path: 'hotel-points/list', isActive: true},
        ]}
      >
        Movimentações
      </PageTitle>
      <HotelPointsListContent />
    </>
  )
}

export {HotelPointsList}
